import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { getGameContent, saveLiveResult } from "../../../functions/crossroadsApi";
import { correctLetter, resolveCrossroadsTheme, revealFor, seededShuffle } from "../../../utils/crossroadsTheme";
import { LIVE_RELEASE_LEAD_MS } from "../../../components/crossroads/JourneyScene";
import {
  countVotes,
  liveChannelFor,
  voteKey,
  newRunId,
  onlineCount,
  resetLiveRun,
  setLiveState,
  subscribeLiveState,
  subscribeParticipants,
  subscribeRunVotes,
  subscribeServerOffset,
} from "../../../utils/liveFirebase";

const resultsKey = (runId) => `cr_live_results_${runId}`;
function readResults(runId) {
  try {
    return JSON.parse(sessionStorage.getItem(resultsKey(runId)) || "{}");
  } catch {
    return {};
  }
}

/**
 * Live results the host couldn't save yet (offline, server error). Kept in localStorage so they
 * survive a refresh, and retried until the server accepts them — never silently dropped.
 */
const pendingKey = (fbId) => `cr_live_pending_${fbId}`;
function readPending(fbId) {
  try {
    const list = JSON.parse(localStorage.getItem(pendingKey(fbId)) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
function writePending(fbId, list) {
  try {
    if (list.length) localStorage.setItem(pendingKey(fbId), JSON.stringify(list));
    else localStorage.removeItem(pendingKey(fbId));
  } catch {
    /* storage unavailable: the in-memory queue still retries while the page is open */
  }
}
const sameResult = (a, b) => a.runId === b.runId && String(a.questionId) === String(b.questionId);
const RETRY_MS = 10000;

export const PHASE_LABEL = {
  lobby: "Waiting to start",
  intro: "Introduction showing",
  voting: "Voting open",
  closed: "Voting closed",
  reveal: "Answer revealed",
  learning: "Did You Know? showing",
  complete: "Game finished",
};

/**
 * Everything the host needs to run a Live Audience game (PRD 2.2 / 7.7): the shared live channel
 * state, players, votes, and the actions that move the game on. Used by Live Control and by the
 * full-screen presenter view, so both drive the exact same flow.
 *
 * `next` / `previous` walk the steps in order:
 *   lobby → intro → [voting → closed → reveal → learning] × crossroads → complete
 * (every crossroads has its "Did You Know?" step — it is never skipped).
 */
export default function useLiveHost() {
  const token = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const organizationId = useSelector(selectOrganizationId);
  // This session's own channel — only its players follow this host.
  const fbId = token ? liveChannelFor(organizationId, sessionId) : null;

  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [live, setLive] = useState(undefined);
  const [participants, setParticipants] = useState({});
  const [runVotes, setRunVotes] = useState({});
  const [offset, setOffset] = useState(0);
  const [results, setResults] = useState({});
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const initRef = useRef(false);
  const closingRef = useRef(false);
  const [unsaved, setUnsaved] = useState([]);
  const flushingRef = useRef(false);

  useEffect(() => {
    if (fbId) setUnsaved(readPending(fbId));
  }, [fbId]);

  const queueUnsaved = useCallback(
    (item) => {
      setUnsaved((prev) => {
        const next = [...prev.filter((x) => !sameResult(x, item)), item];
        writePending(fbId, next);
        return next;
      });
    },
    [fbId]
  );

  /** Try every unsaved live result again; whatever still fails stays queued. */
  const flushUnsaved = useCallback(async () => {
    if (!token || !fbId || flushingRef.current) return;
    const queue = readPending(fbId);
    if (!queue.length) return;
    flushingRef.current = true;
    const left = [];
    for (const item of queue) {
      try {
        await saveLiveResult({ token, ...item });
      } catch {
        left.push(item);
      }
    }
    flushingRef.current = false;
    writePending(fbId, left);
    setUnsaved(left);
  }, [token, fbId]);

  // Retry on reconnect, on load, and every few seconds while anything is unsaved.
  useEffect(() => {
    if (!unsaved.length) return undefined;
    const onOnline = () => flushUnsaved();
    window.addEventListener("online", onOnline);
    const t = window.setInterval(flushUnsaved, RETRY_MS);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(t);
    };
  }, [unsaved.length, flushUnsaved]);
  useEffect(() => {
    flushUnsaved();
  }, [flushUnsaved]);

  useEffect(() => {
    if (!token) return;
    // The host screen needs each crossroads' answer to reveal it.
    getGameContent({ token, includeAnswers: true })
      .then(setContent)
      .catch((err) => setLoadError(err.message || "Failed to load the game."));
  }, [token]);

  useEffect(() => subscribeLiveState(fbId, setLive), [fbId]);
  useEffect(() => subscribeParticipants(fbId, setParticipants), [fbId]);
  useEffect(() => subscribeServerOffset(setOffset), []);
  useEffect(() => subscribeRunVotes(fbId, live?.runId, setRunVotes), [fbId, live?.runId]);

  const theme = useMemo(() => resolveCrossroadsTheme(content?.settings), [content]);
  const questions = useMemo(() => {
    const list = content?.questions || [];
    return theme.shuffle && live?.runId ? seededShuffle(list, `live-${live.runId}`) : list;
  }, [content, theme.shuffle, live?.runId]);
  const total = questions.length;
  const phase = live?.phase || "lobby";
  const index = Math.min(live?.index ?? 0, Math.max(0, total - 1));
  const question = ["voting", "closed", "reveal", "learning"].includes(phase) ? questions[index] : null;
  const liveCounts = countVotes(runVotes[voteKey(index)]);
  const counts = (question && results[question.id]) || liveCounts;
  const online = onlineCount(participants);
  // The countdown starts once players' boards have brought the crossroads on screen.
  const votingStarted = phase === "voting" && Date.now() + offset >= (live?.votingStartsAt || 0);
  const remainingMs =
    phase === "voting" && live?.votingEndsAt
      ? votingStarted
        ? Math.max(0, live.votingEndsAt - (Date.now() + offset))
        : theme.votingSeconds * 1000
      : 0;
  const isLast = index + 1 >= total;

  // First visit for this session: create a run so players have something to wait in.
  useEffect(() => {
    if (!content || live === undefined || !fbId || initRef.current) return;
    initRef.current = true;
    if (!live) resetLiveRun(fbId, newRunId(), (content.questions || []).length);
  }, [content, live, fbId]);

  useEffect(() => {
    if (live?.runId) setResults(readResults(live.runId));
  }, [live?.runId]);
  useEffect(() => {
    if (!live?.runId) return;
    try {
      sessionStorage.setItem(resultsKey(live.runId), JSON.stringify(results));
    } catch {
      /* ignore */
    }
  }, [results, live?.runId]);

  useEffect(() => {
    if (phase !== "voting") return undefined;
    const t = window.setInterval(() => setTick((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [phase]);

  const run = useCallback(async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      Swal.fire("Something went wrong", err.message || "Please try again.", "error");
    } finally {
      setBusy(false);
    }
  }, []);

  const startGame = () => run(() => setLiveState(fbId, { phase: "intro" }));

  const releaseQuestion = (i) =>
    run(async () => {
      const q = questions[i];
      if (!q) return;
      closingRef.current = false;
      // Players first walk to this crossroads (from the intro, or on from the previous one);
      // reopening the same crossroads needs no walk. Voting time starts after that.
      const reopening = phase === "closed" && i === index;
      const lead = reopening ? 0 : phase === "lobby" || phase === "intro" ? LIVE_RELEASE_LEAD_MS.first : LIVE_RELEASE_LEAD_MS.next;
      const votingStartsAt = Date.now() + offset + lead;
      await setLiveState(fbId, {
        phase: "voting",
        index: i,
        questionId: q.id,
        total,
        votingStartsAt,
        votingEndsAt: votingStartsAt + theme.votingSeconds * 1000,
      });
    });

  const closeVoting = useCallback(() => {
    if (!question || closingRef.current) return;
    closingRef.current = true;
    const c = countVotes(runVotes[voteKey(index)]);
    setResults((prev) => ({ ...prev, [question.id]: { ...c, correct: correctLetter(question) } }));
    setLiveState(fbId, { phase: "closed", votingEndsAt: null });
  }, [question, runVotes, fbId, index]);

  useEffect(() => {
    if (phase === "voting" && live?.votingEndsAt && remainingMs <= 0) closeVoting();
  }, [phase, live?.votingEndsAt, remainingMs, closeVoting]);

  const reveal = () =>
    run(async () => {
      // Players' content carries no answers: the host hands this crossroads' answer and learning
      // content to the room only now, at the reveal.
      await setLiveState(fbId, {
        phase: "reveal",
        [`reveals/${voteKey(index)}`]: revealFor(question),
      });
      // The server tallies the votes itself; the host only says which crossroads was revealed.
      const item = { runId: live.runId, questionId: question.id, questionIndex: index };
      saveLiveResult({ token, ...item }).catch((err) => {
        console.warn("Live result not saved — queued for retry:", err.message);
        queueUnsaved(item);
      });
    });

  const showLearning = () => run(() => setLiveState(fbId, { phase: "learning" }));
  const finishGame = () => run(() => setLiveState(fbId, { phase: "complete" }));
  const afterQuestion = () => (isLast ? finishGame() : releaseQuestion(index + 1));

  // Go back to a crossroads' last step (its "Did You Know?", or its answer if it has none).
  const backToEndOf = (i) =>
    run(() =>
      setLiveState(fbId, {
        phase: "learning",
        index: i,
        questionId: questions[i]?.id ?? null,
        votingEndsAt: null,
      })
    );

  const restart = async () => {
    if (phase !== "lobby" && phase !== "complete") {
      const r = await Swal.fire({
        title: "Restart the live game?",
        text: "This ends the current game for every player and returns everyone to the waiting screen.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Restart",
      });
      if (!r.isConfirmed) return;
    }
    closingRef.current = false;
    setResults({});
    run(() => resetLiveRun(fbId, newRunId(), total));
  };

  const correct = question ? correctLetter(question) : null;

  /** The next step — label for the button, and what it does. null = nothing further. */
  let nextStep = null;
  if (phase === "lobby" && total) nextStep = { label: "Start Game", icon: "fa-play", go: startGame };
  else if (phase === "intro") nextStep = { label: "Release Crossroads 1", icon: "fa-paper-plane", go: () => releaseQuestion(0) };
  else if (phase === "voting") nextStep = { label: "Close Voting", icon: "fa-lock", go: closeVoting };
  else if (phase === "closed") nextStep = { label: correct ? "Reveal Answer" : "Show Results", icon: "fa-eye", go: reveal };
  else if (phase === "reveal") nextStep = { label: "Did You Know?", icon: "fa-lightbulb", go: showLearning };
  else if (phase === "learning") {
    nextStep = isLast
      ? { label: "Finish Game", icon: "fa-flag-checkered", go: finishGame }
      : { label: `Release Crossroads ${index + 2}`, icon: "fa-paper-plane", go: afterQuestion };
  }

  /** One step back (players' screens follow). null = already at the start. */
  let prevStep = null;
  if (phase === "intro") prevStep = { label: "Back to waiting", go: () => run(() => setLiveState(fbId, { phase: "lobby" })) };
  else if (phase === "voting") {
    prevStep =
      index === 0
        ? { label: "Back to introduction", go: () => run(() => setLiveState(fbId, { phase: "intro", votingEndsAt: null })) }
        : { label: `Back to Crossroads ${index}`, go: () => backToEndOf(index - 1) };
  } else if (phase === "closed") prevStep = { label: "Reopen voting", go: () => releaseQuestion(index) };
  else if (phase === "reveal") prevStep = { label: "Back to voting closed", go: () => run(() => setLiveState(fbId, { phase: "closed" })) };
  else if (phase === "learning") prevStep = { label: "Back to the answer", go: () => run(() => setLiveState(fbId, { phase: "reveal" })) };
  else if (phase === "complete" && total) prevStep = { label: `Back to Crossroads ${total}`, go: () => backToEndOf(total - 1) };

  const summary = useMemo(() => {
    let votes = 0;
    let scoredVotes = 0;
    let correctVotes = 0;
    const voters = new Set();
    Object.values(runVotes || {}).forEach((byPid) => Object.keys(byPid || {}).forEach((pid) => voters.add(pid)));
    const rows = questions.map((q, i) => {
      const r = results[q.id] || { ...countVotes(runVotes[voteKey(i)]), correct: correctLetter(q) };
      votes += r.total;
      // Choice-based crossroads (correct = null) have no right answer: left out of accuracy.
      if (!r.correct) return { i, q, ...r, pct: null };
      const c = r.correct === "A" ? r.a : r.b;
      scoredVotes += r.total;
      correctVotes += c;
      return { i, q, ...r, pct: r.total ? Math.round((c / r.total) * 100) : 0 };
    });
    return { votes, voters: voters.size, accuracy: scoredVotes ? Math.round((correctVotes / scoredVotes) * 100) : 0, rows };
  }, [questions, results, runVotes]);

  return {
    token,
    fbId,
    unsavedResults: unsaved.length,
    retryUnsaved: flushUnsaved,
    content,
    loadError,
    live,
    participants,
    runVotes,
    theme,
    questions,
    total,
    phase,
    index,
    question,
    counts,
    online,
    remainingMs,
    votingStarted,
    offset,
    isLast,
    correct,
    busy,
    summary,
    run,
    startGame,
    releaseQuestion,
    closeVoting,
    reveal,
    showLearning,
    finishGame,
    restart,
    nextStep,
    prevStep,
  };
}
